// src/components/events/EventAllGrid.js
import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { db } from "../../firebase";
import { FaEye } from "react-icons/fa";
import { collection, onSnapshot } from "firebase/firestore";
import {
  getStorage,
  ref as storageRef,
  getDownloadURL,
} from "firebase/storage";

// 탭별 정렬 옵션 미리 선언 (useEffect deps 경고 제거용)
const SORT_OPTIONS = {
  ongoing: [
    { value: "startDesc", label: "최근시작일순" },
    { value: "endAsc", label: "종료임박순" },
    { value: "createdDesc", label: "등록일순" },
  ],
  upcoming: [
    { value: "createdDesc", label: "등록일순" },
    { value: "createdAsc", label: "오래된순" },
  ],
  ended: [
    { value: "endDesc", label: "종료일순" },
    { value: "createdDesc", label: "등록일순" },
  ],
};

export default function EventAllGrid({ onCount }) {
  const [events, setEvents] = useState([]);
  const [bannerUrls, setBannerUrls] = useState({}); // { [eventId]: HTTP 다운로드 URL }
  const [filter, setFilter] = useState("ongoing");
  const [sortOption, setSortOption] = useState(SORT_OPTIONS.ongoing[0].value);

  // 0) Firestore 'events' 컬렉션 구독
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "events"), (snap) => {
      setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  // 1) bannerImageUrl → HTTP URL 로변환 + localStorage 캐싱
  useEffect(() => {
    const storage = getStorage();

    events.forEach((ev) => {
      if (bannerUrls[ev.id]) return;

      const stored = ev.bannerImageUrl || "";
      const isHttpUrl =
        stored.startsWith("http://") || stored.startsWith("https://");
      if (isHttpUrl) {
        setBannerUrls((prev) => ({ ...prev, [ev.id]: stored }));
        try {
          localStorage.setItem(`eventBanner_${ev.id}`, stored);
        } catch {}
        return;
      }

      const cached = localStorage.getItem(`eventBanner_${ev.id}`);
      if (cached) {
        setBannerUrls((prev) => ({ ...prev, [ev.id]: cached }));
        return;
      }

      const pathToFile = stored || `events/${ev.id}/${ev.id}_banner`;
      const refObj = storageRef(storage, pathToFile);
      getDownloadURL(refObj)
        .then((url) => {
          setBannerUrls((prev) => ({ ...prev, [ev.id]: url }));
          try {
            localStorage.setItem(`eventBanner_${ev.id}`, url);
          } catch {}
        })
        .catch((err) => {
          console.warn(
            `Storage에서 이벤트 배너 URL 가져오기 실패(${ev.id}):`,
            err
          );
        });
    });
  }, [events, bannerUrls]); // bannerUrls 추가

  // 2) 탭(filter) 바뀔 때마다 기본 정렬 옵션 리셋
  useEffect(() => {
    setSortOption(SORT_OPTIONS[filter][0].value);
  }, [filter]);

  // 3) 날짜 파싱 helpers
  const parseStart = (str) => {
    if (typeof str !== "string") return null;
    const d = new Date(str.replace(/\./g, "-") + "T00:00:00");
    return isNaN(d.getTime()) ? null : d;
  };
  const parseEnd = (str) => {
    if (typeof str !== "string") return null;
    const d = new Date(str.replace(/\./g, "-") + "T23:59:59");
    return isNaN(d.getTime()) ? null : d;
  };
  const msPerDay = 1000 * 60 * 60 * 24;

  // 4) 필터링
  const filtered = useMemo(() => {
    const now = new Date();
    return events.filter((ev) => {
      const sd = parseStart(ev.startDate);
      const ed = parseEnd(ev.endDate);
      if (filter === "ongoing") {
        return sd && ed && now >= sd && now <= ed;
      }
      if (filter === "upcoming") {
        return sd && now < sd;
      }
      if (filter === "ended") {
        return ed && now > ed;
      }
      return true;
    });
  }, [events, filter]);

  // 5) 정렬
  const sorted = useMemo(() => {
    const arr = [...filtered];
    switch (sortOption) {
      case "startDesc":
        arr.sort((a, b) => parseStart(b.startDate) - parseStart(a.startDate));
        break;
      case "endAsc":
        arr.sort((a, b) => parseEnd(a.endDate) - parseEnd(b.endDate));
        break;
      case "endDesc":
        arr.sort((a, b) => parseEnd(b.endDate) - parseEnd(a.endDate));
        break;
      case "createdAsc":
        arr.sort((a, b) => a.createdAt?.toDate() - b.createdAt?.toDate());
        break;
      case "createdDesc":
        arr.sort((a, b) => b.createdAt?.toDate() - a.createdAt?.toDate());
        break;
      default:
        break;
    }
    return arr;
  }, [filtered, sortOption]);

  // 6) 개수 콜백
  useEffect(() => {
    onCount?.(sorted.length);
  }, [sorted, onCount]);

  const tabs = [
    { key: "ongoing", label: "진행중" },
    { key: "upcoming", label: "진행예정" },
    { key: "ended", label: "종료" },
  ];

  return (
    <section className="container mx-auto px-6 pb-6">
      {/* 탭 & 정렬 드롭다운 */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex space-x-6 border-b border-gray-700 text-white text-base">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`pb-2 transition-colors ${
                filter === tab.key
                  ? "border-b-4 border-red-500 text-red-400"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <select
          value={sortOption}
          onChange={(e) => setSortOption(e.target.value)}
          className="text-sm bg-neutral-800 text-white border border-gray-600 rounded px-3 py-2"
        >
          {SORT_OPTIONS[filter].map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* 이벤트 그리드 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {sorted.length > 0 ? (
          sorted.map((ev) => {
            const bannerSrc = bannerUrls[ev.id] || "";
            const sd = parseStart(ev.startDate);
            const ed = parseEnd(ev.endDate);
            let status = "",
              daysLeft = 0;
            const now = new Date();

            if (sd && ed) {
              if (now < sd) {
                status = "진행예정";
                daysLeft = Math.floor((sd - now) / msPerDay);
              } else if (now > ed) {
                status = "종료";
              } else {
                status = "진행중";
                daysLeft = Math.floor((ed - now) / msPerDay);
              }
            }

            let extraBadge = null;
            if (status === "진행중") {
              if (daysLeft >= 1 && daysLeft <= 3) {
                extraBadge = `D-${daysLeft}`;
              } else if (daysLeft === 0) {
                extraBadge = "마감임박";
              }
            }

            const statusColor =
              status === "진행중"
                ? "bg-green-600"
                : status === "진행예정"
                ? "bg-yellow-600"
                : "bg-gray-600";

            return (
              <Link
                key={ev.id}
                to={`/events/detail/${ev.id}`}
                className="relative group bg-[#1f1f1f] rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition flex flex-col"
              >
                {/* 상태 배지 */}
                <div className="absolute top-3 left-3 flex space-x-2 z-10">
                  {!!status && (
                    <span
                      className={`px-2 py-1 rounded-full text-sm font-semibold text-white ${statusColor}`}
                    >
                      {status}
                    </span>
                  )}
                  {extraBadge && (
                    <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {extraBadge}
                    </span>
                  )}
                </div>

                {/* 배너 */}
                <div className="w-full aspect-img bg-black overflow-hidden flex items-center justify-center">
                  {bannerSrc ? (
                    <img
                      src={bannerSrc}
                      alt={ev.title}
                      loading="lazy"
                      className="object-contain w-full h-full"
                    />
                  ) : (
                    <span className="text-gray-500">이미지 없음</span>
                  )}
                </div>

                {/* 제목/날짜/조회수 */}
                <div className="p-4 flex flex-col justify-between min-h-28">
                  <h3 className="text-xl font-bold text-white truncate py-2">
                    {ev.title}
                  </h3>
                  <div className="text-gray-400 text-base flex justify-between items-center">
                    <span>
                      {ev.startDate} ~ {ev.endDate}
                    </span>
                    <span className="flex items-center">
                      <FaEye className="mr-2" />
                      {ev.viewCount || 0}
                    </span>
                  </div>
                </div>
              </Link>
            );
          })
        ) : (
          <p className="col-span-full text-center text-gray-500">
            해당 조건의 이벤트가 없습니다.
          </p>
        )}
      </div>
    </section>
  );
}
